/**
 * Task 224 — PURE deterministic strategy-candidate generation for the client
 * Creative Intelligence decision board.
 *
 * Input is the Task 221 cross-platform content groups (already returned by
 * `list-creative-intelligence-content-groups`). This module derives strategy
 * CANDIDATES from those existing signals ONLY — per-platform performance /
 * fatigue, spend / impressions / ROAS, best/worst platform, match quality, and
 * asset-link state. It is:
 *   - deterministic — no clock, no randomness, no network, no AI;
 *   - human-led — it proposes candidates; a person selects one (no auto-decision);
 *   - side-effect-free — it creates no content, tasks, approvals, or production.
 *
 * Confidence is a function of DATA COMPLETENESS + MATCH QUALITY, never a model.
 */

import type {
  CreativeIntelligenceContentGroup,
  CreativeIntelligencePlatform,
  CreativeIntelligencePlatformSlice,
} from '../../api/types';

export type CreativeStrategyKind =
  | 'scale_winner'
  | 'stop_waste'
  | 'refresh_fatigued'
  | 'adapt_platform'
  | 'fix_matching'
  | 'needs_more_data';

export type CreativeStrategyConfidence = 'high' | 'medium' | 'low';

export interface CreativeStrategyCandidate {
  /** Stable per (kind, group): `${kind}:${group_id}`. */
  readonly id: string;
  readonly kind: CreativeStrategyKind;
  readonly group_id: string;
  readonly group_display_name: string;
  /** Platforms whose signals support this candidate (winner/waste/fatigued/...). */
  readonly supporting_platforms: ReadonlyArray<CreativeIntelligencePlatform>;
  /** Confidence from data completeness + match quality (NOT AI). */
  readonly confidence: CreativeStrategyConfidence;
  /** Short factual "why" lines, drawn from the content-group signals. */
  readonly evidence: ReadonlyArray<string>;
}

/** Named thresholds — documented + unit-tested (no magic numbers). */
export const CREATIVE_STRATEGY_THRESHOLDS = {
  /**
   * Group impressions (summed across platforms with data) at/above which the
   * data is "complete enough" to (a) propose growth strategies (scale / adapt)
   * and (b) lift confidence above `low`. Mirrors the Task 221 EF's
   * `MIN_IMPRESSIONS_FOR_DATA`.
   */
  MIN_IMPRESSIONS_FOR_CONFIDENCE: 1000,
} as const;

/** Display ordering: most urgent / actionable first. Deterministic + documented. */
const KIND_PRIORITY: Readonly<Record<CreativeStrategyKind, number>> = {
  stop_waste: 0,
  scale_winner: 1,
  refresh_fatigued: 2,
  adapt_platform: 3,
  fix_matching: 4,
  needs_more_data: 5,
};

const CONFIDENCE_RANK: Readonly<Record<CreativeStrategyConfidence, number>> = {
  high: 0,
  medium: 1,
  low: 2,
};

function dataSlices(
  group: CreativeIntelligenceContentGroup,
): ReadonlyArray<CreativeIntelligencePlatformSlice> {
  return group.platforms.filter((p) => p.availability === 'has_data');
}

function totalImpressions(group: CreativeIntelligenceContentGroup): number {
  return dataSlices(group).reduce((n, p) => n + p.impressions, 0);
}

function fmtRoas(roas: number | null): string {
  return roas === null ? 'n/a' : roas.toFixed(2);
}

/**
 * Confidence from data completeness + match quality — never a model.
 *   - `unmatched`, or below the impression floor → low
 *   - `asset_match` with enough data → high
 *   - name-based match (exact_name / alias / fuzzy) with enough data → medium
 */
export function strategyConfidence(
  group: CreativeIntelligenceContentGroup,
): CreativeStrategyConfidence {
  if (group.match_quality === 'unmatched') return 'low';
  if (totalImpressions(group) < CREATIVE_STRATEGY_THRESHOLDS.MIN_IMPRESSIONS_FOR_CONFIDENCE) {
    return 'low';
  }
  if (group.match_quality === 'asset_match') return 'high';
  return 'medium';
}

/**
 * Build the deterministic strategy candidates for a set of content groups,
 * sorted by urgency (kind) then confidence then group display name then id
 * (stable + deterministic).
 */
export function buildStrategyCandidates(
  groups: ReadonlyArray<CreativeIntelligenceContentGroup>,
): ReadonlyArray<CreativeStrategyCandidate> {
  const out: CreativeStrategyCandidate[] = [];

  for (const group of groups) {
    const slices = dataSlices(group);
    const conf = strategyConfidence(group);
    const dataComplete =
      totalImpressions(group) >= CREATIVE_STRATEGY_THRESHOLDS.MIN_IMPRESSIONS_FOR_CONFIDENCE;

    const push = (
      kind: CreativeStrategyKind,
      platforms: ReadonlyArray<CreativeIntelligencePlatform>,
      evidence: ReadonlyArray<string>,
      confidence: CreativeStrategyConfidence = conf,
    ): void => {
      out.push({
        id: `${kind}:${group.id}`,
        kind,
        group_id: group.id,
        group_display_name: group.display_name,
        supporting_platforms: platforms,
        confidence,
        evidence,
      });
    };

    // Unmatched ads cannot be analyzed AS content — link them first. Nothing
    // else fires for this group (the signals aren't tied to a known creative).
    if (group.match_quality === 'unmatched') {
      push(
        'fix_matching',
        slices.map((s) => s.platform),
        ['هذه الإعلانات غير مرتبطة بأصل إبداعي؛ اربطها أولاً لتحليلها كمحتوى.'],
        'low',
      );
      continue;
    }

    const winners = slices.filter((s) => s.performance_signal === 'winner');
    const wasters = slices.filter((s) => s.performance_signal === 'waste');
    const weak = slices.filter((s) => s.performance_signal === 'weak');
    const fatigued = slices.filter((s) => s.fatigue_signal === 'fatigued');

    // Stop waste — spend with zero conversions is actionable regardless of volume.
    if (wasters.length > 0) {
      push(
        'stop_waste',
        wasters.map((s) => s.platform),
        wasters.map((s) => `${s.platform}: إنفاق ${s.spend} بدون تحويلات`),
      );
    }

    // Refresh fatigued — the fatigue signal already needs ≥ 4 days of data.
    if (fatigued.length > 0) {
      push(
        'refresh_fatigued',
        fatigued.map((s) => s.platform),
        fatigued.map((s) => `${s.platform}: إجهاد إبداعي (انخفاض CTR مع ارتفاع الحجم)`),
      );
    }

    // Growth strategies require enough delivery to trust the signal.
    if (dataComplete && winners.length > 0) {
      push(
        'scale_winner',
        winners.map((s) => s.platform),
        winners.map((s) => `${s.platform}: أداء رابح (ROAS ${fmtRoas(s.roas)})`),
      );

      const lagging = [...weak, ...wasters];
      if (lagging.length > 0) {
        push(
          'adapt_platform',
          [...winners, ...lagging].map((s) => s.platform),
          [
            `ناجح على ${winners.map((s) => s.platform).join('، ')}`,
            `ومتعثّر على ${lagging.map((s) => s.platform).join('، ')}`,
          ],
        );
      }
    }

    // A fuzzy match is uncertain — confirm/relink before trusting the grouping.
    if (group.match_quality === 'fuzzy') {
      push(
        'fix_matching',
        slices.map((s) => s.platform),
        ['المطابقة تقريبية (fuzzy)؛ أكّد الربط بالأصل الإبداعي لضمان دقة التحليل.'],
        'low',
      );
    }

    // Genuinely insufficient data AND no spend/fatigue signal to act on.
    if (
      !dataComplete &&
      winners.length === 0 &&
      wasters.length === 0 &&
      fatigued.length === 0 &&
      slices.length > 0
    ) {
      push(
        'needs_more_data',
        slices.map((s) => s.platform),
        [`إجمالي الظهور ${totalImpressions(group)} أقل من حد التحليل الموثوق`],
        'low',
      );
    }
  }

  return out
    .slice()
    .sort(
      (a, b) =>
        KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind] ||
        CONFIDENCE_RANK[a.confidence] - CONFIDENCE_RANK[b.confidence] ||
        a.group_display_name.localeCompare(b.group_display_name) ||
        a.id.localeCompare(b.id),
    );
}
